import { useMemo, useState, type FormEvent } from 'react'
import {
  useLibraryComponents,
  useCreateLibraryComponent,
  usePatchLibraryComponent,
  useDeleteLibraryComponent
} from '../hooks/useLibraryComponents'
import {
  ModalShell, Field, inputClass,
  CancelButton, PrimaryButton, DangerButton, ServerError
} from '../components/modal-form'
import { Icon } from '../components/Icon'
import { useMe } from '../hooks/useMe'
import {
  LIBRARY_COMPONENT_CATEGORIES,
  type LibraryComponent,
  type LibraryComponentCategory,
  type LibraryComponentCreate,
  type LibraryComponentPatch
} from '../types'

// AdminLibraryComponents — admin-only page for the org-level Components
// catalog (mig 034 / Library Build PR A). Mirrors AdminHazardLibrary /
// AdminBriefingTopics: grouped by category, click to edit, soft-delete
// preserves any EventComponents rows that came from a slug.

export function AdminLibraryComponents() {
  const { data: me } = useMe()
  const { data: components, isLoading, error } = useLibraryComponents()
  const [editing, setEditing] = useState<LibraryComponent | 'new' | null>(null)

  // Group by category for visual scan; preserve LIBRARY_COMPONENT_CATEGORIES
  // declared order so headers always appear in the canonical sequence
  // (Logistics, Safety, Comms, …) — alphabetic would scatter Safety
  // away from Comms which feels wrong operationally.
  const grouped = useMemo(() => {
    const buckets = new Map<LibraryComponentCategory, LibraryComponent[]>()
    for (const c of components ?? []) {
      const list = buckets.get(c.category) ?? []
      list.push(c)
      buckets.set(c.category, list)
    }
    return LIBRARY_COMPONENT_CATEGORIES
      .map((cat) => ({ category: cat, rows: buckets.get(cat) ?? [] }))
      .filter((g) => g.rows.length > 0)
  }, [components])

  if (me && !me.isAdmin) {
    return (
      <div className="space-y-5">
        <div>
          <h1 className="text-3xl mb-1">Components library</h1>
          <p className="text-ink-soft">Admin only.</p>
        </div>
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
          Editing the components library requires admin permissions.
        </div>
      </div>
    )
  }

  const autoAddCount = components?.filter((c) => c.autoAdd).length ?? 0

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl mb-1">Components library</h1>
          <p className="text-ink-soft">
            Reusable event components — parking, first aid, comms, accessibility, etc.
            When a new event is created, every row marked “auto-add” is copied
            onto it. Edits here do not change components already on existing events.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="px-4 py-2 text-sm font-semibold rounded bg-green text-parchment hover:bg-green/90 transition-colors"
        >
          + New component
        </button>
      </div>

      {isLoading && (
        <div className="text-ink-soft text-sm font-mono">Loading components…</div>
      )}

      {error && (
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
          <div className="font-semibold mb-1">Could not load components</div>
          <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
        </div>
      )}

      {components && components.length === 0 && (
        <div className="bg-white border border-line rounded p-10 text-center">
          <div
            className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
            style={{ background: '#dcebe5', color: '#0a4a3a' }}
          >
            <Icon name="box" size={22} />
          </div>
          <h3 className="font-display text-lg mb-1">No library components yet</h3>
          <p className="text-sm text-ink-soft mb-4 max-w-md mx-auto">
            Add a component here and it becomes available for every event
            to pull onto its Components tab. Mark a row “auto-add” to have
            it copied to every new event automatically.
          </p>
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="px-4 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 transition-colors"
          >
            + Add the first component
          </button>
        </div>
      )}

      {components && components.length > 0 && (
        <div className="text-xs text-ink-soft font-mono">
          {components.length} component{components.length === 1 ? '' : 's'} · {autoAddCount} auto-add
        </div>
      )}

      {grouped.map(({ category, rows }) => (
        <section key={category} className="bg-white border border-line rounded overflow-hidden">
          <header className="px-4 py-2.5 border-b border-line bg-parchment/60 flex items-baseline justify-between">
            <h2 className="text-sm font-semibold">{category}</h2>
            <span className="text-xs text-ink-soft font-mono">
              {rows.length} component{rows.length === 1 ? '' : 's'}
            </span>
          </header>
          <div className="divide-y divide-line">
            {rows.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setEditing(c)}
                className="w-full text-left px-4 py-3 flex items-start gap-4 hover:bg-parchment/40 transition-colors"
              >
                <Icon name="box" size={16} className="text-slate flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="font-semibold text-sm">{c.name}</span>
                    <span className="text-[10px] text-ink-soft font-mono">{c.slug}</span>
                  </div>
                  {c.description && (
                    <div className="text-xs text-ink-soft mt-0.5 line-clamp-1">
                      {c.description}
                    </div>
                  )}
                </div>
                {c.autoAdd && (
                  <span
                    className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded whitespace-nowrap"
                    style={{ background: '#dcebe5', color: '#0a4a3a' }}
                  >
                    auto-add
                  </span>
                )}
              </button>
            ))}
          </div>
        </section>
      ))}

      {editing && (
        <ComponentEditor
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

// ---- Editor modal --------------------------------------------------------

function ComponentEditor({
  initial, onClose
}: {
  initial: LibraryComponent | null
  onClose: () => void
}) {
  const isEditing = initial != null
  const createMut = useCreateLibraryComponent()
  const patchMut  = usePatchLibraryComponent()
  const deleteMut = useDeleteLibraryComponent()

  const [slug, setSlug]               = useState(initial?.slug ?? '')
  const [name, setName]               = useState(initial?.name ?? '')
  const [category, setCategory]       = useState<LibraryComponentCategory>(initial?.category ?? 'Logistics')
  const [icon, setIcon]               = useState(initial?.icon ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [autoAdd, setAutoAdd]         = useState(initial?.autoAdd ?? false)

  // Auto-suggest slug from name on the CREATE path only — once a slug
  // exists (edit mode), don't touch it. Keeps the contract stable.
  function onNameChange(v: string) {
    setName(v)
    if (!isEditing && (slug === '' || slug === slugify(name))) {
      setSlug(slugify(v))
    }
  }

  const trimmedSlug = slug.trim()
  const trimmedName = name.trim()
  const fieldErrors: { slug?: string; name?: string } = {}
  if (!isEditing) {
    if (trimmedSlug.length === 0) fieldErrors.slug = 'Slug is required'
    else if (trimmedSlug.length > 80) fieldErrors.slug = 'Slug must be 80 chars or fewer'
    else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(trimmedSlug)) {
      fieldErrors.slug = 'Slug must be kebab-case (lowercase letters, digits, hyphens)'
    }
  }
  if (trimmedName.length === 0) fieldErrors.name = 'Name is required'
  else if (trimmedName.length > 200) fieldErrors.name = 'Name must be 200 chars or fewer'

  const isValid = Object.keys(fieldErrors).length === 0
  const saving = createMut.isPending || patchMut.isPending
  const deleting = deleteMut.isPending
  const serverError = (createMut.error ?? patchMut.error ?? deleteMut.error) as Error | null

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!isValid || saving) return
    if (isEditing && initial) {
      const patch: LibraryComponentPatch = {
        name: trimmedName,
        category,
        icon: icon.trim() || null,
        description: description.trim() || null,
        autoAdd
      }
      patchMut.mutate({ id: initial.id, body: patch }, { onSuccess: () => onClose() })
    } else {
      const body: LibraryComponentCreate = {
        slug: trimmedSlug,
        name: trimmedName,
        category,
        icon: icon.trim() || null,
        description: description.trim() || null,
        autoAdd
      }
      createMut.mutate(body, { onSuccess: () => onClose() })
    }
  }

  function handleDelete() {
    if (!initial) return
    const ok = window.confirm(
      `Remove "${initial.name}" from the library? Components already added to existing events stay — only future "Add from library" lookups stop seeing it.`
    )
    if (!ok) return
    deleteMut.mutate(initial.id, { onSuccess: () => onClose() })
  }

  return (
    <ModalShell
      title={isEditing ? 'Edit library component' : 'New library component'}
      onClose={onClose}
      onSubmit={handleSubmit}
      size="lg"
      footer={
        <>
          {isEditing && (
            <DangerButton onClick={handleDelete} disabled={saving || deleting}>
              {deleting ? 'Removing…' : 'Remove'}
            </DangerButton>
          )}
          <CancelButton onClick={onClose} disabled={saving} />
          <PrimaryButton disabled={!isValid || saving}>
            {saving ? 'Saving…' : isEditing ? 'Save changes' : 'Add component'}
          </PrimaryButton>
        </>
      }
      body={
        <div className="space-y-4">
          <Field label="Name" error={fieldErrors.name}>
            <input
              type="text"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              maxLength={200}
              autoFocus
              required
              className={inputClass(!!fieldErrors.name)}
              placeholder="e.g. Car parking & access"
            />
          </Field>

          <Field
            label="Slug"
            error={fieldErrors.slug}
            hint={isEditing ? 'Locked — slugs are the public contract' : 'Auto-suggested from name. Kebab-case.'}
          >
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              maxLength={80}
              required={!isEditing}
              disabled={isEditing}
              className={inputClass(!!fieldErrors.slug) + (isEditing ? ' opacity-60 cursor-not-allowed' : '')}
              placeholder="car-parking-and-access"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as LibraryComponentCategory)}
                className={inputClass(false)}
              >
                {LIBRARY_COMPONENT_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>
            <Field label="Icon" hint="Lucide name (optional)">
              <input
                type="text"
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                maxLength={40}
                className={inputClass(false)}
                placeholder="box"
              />
            </Field>
          </div>

          <Field label="Description (optional)">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className={inputClass(false)}
              placeholder="Short description shown on the per-event Components tab."
            />
          </Field>

          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={autoAdd}
              onChange={(e) => setAutoAdd(e.target.checked)}
              disabled={saving}
              className="w-4 h-4 mt-0.5 accent-green cursor-pointer disabled:cursor-not-allowed"
            />
            <div>
              <div className="text-sm font-semibold">Auto-add to new events</div>
              <div className="text-xs text-ink-soft">
                When ticked, this component is automatically copied onto every
                new event when it's created. Existing events are NOT backfilled.
              </div>
            </div>
          </label>

          {serverError && <ServerError title="Could not save" error={serverError} />}
        </div>
      }
    />
  )
}

// kebab-case from a free-text name. Strips non-alphanumeric, collapses
// runs of dashes, trims leading/trailing dashes. Used only for the
// create path's slug auto-suggest — admin can edit before submit.
function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}
